import { Card, CardContent } from "@/components/ui/card";

interface CardGridSkeletonProps {
  hasHeader?: boolean;
  hasMetrics?: boolean;
  metricsCount?: number;
  cardCount?: number;
  columns?: number;
}

export function CardGridSkeleton({
  hasHeader = true,
  hasMetrics = true,
  metricsCount = 4,
  cardCount = 6,
  columns = 3,
}: CardGridSkeletonProps) {
  const gridCols = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  }[columns as 1 | 2 | 3 | 4] || "grid-cols-1 md:grid-cols-3";

  return (
    <div className="space-y-6 animate-pulse">
      {/* Header skeleton */}
      {hasHeader && (
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-48 bg-slate-200 rounded-lg" />
            <div className="h-4 w-72 bg-slate-100 rounded" />
          </div>
          <div className="flex gap-2">
            <div className="h-10 w-40 bg-slate-200 rounded-lg" />
          </div>
        </div>
      )}

      {/* Metrics Cards skeleton */}
      {hasMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: metricsCount }).map((_, i) => (
            <Card key={i} className="border-slate-200/70 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-2">
                    <div className="h-3 w-20 bg-slate-100 rounded" />
                    <div className="h-7 w-12 bg-slate-200 rounded" />
                  </div>
                  <div className="h-8 w-8 bg-slate-50 rounded-lg" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Search Bar skeleton */}
      <div className="h-10 max-w-md bg-slate-100 rounded-lg border border-slate-200/60" />

      {/* Grid skeleton */}
      <div className={`grid gap-6 ${gridCols}`}>
        {Array.from({ length: cardCount }).map((_, i) => (
          <Card key={i} className="border-slate-200/60 shadow-sm overflow-hidden">
            <CardContent className="p-0">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex gap-4">
                    <div className="h-12 w-12 rounded-full bg-slate-100" />
                    <div className="space-y-2">
                      <div className="h-4 w-32 bg-slate-200 rounded" />
                      <div className="h-3 w-24 bg-slate-100 rounded" />
                    </div>
                  </div>
                  <div className="h-6 w-12 bg-slate-50 rounded" />
                </div>
                
                <div className="space-y-3 mt-6">
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-slate-100" />
                    <div className="h-3 w-3/4 bg-slate-50 rounded" />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 rounded-full bg-slate-100" />
                    <div className="h-3 w-1/2 bg-slate-50 rounded" />
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-50 flex justify-between items-center">
                  <div className="h-3 w-20 bg-slate-100 rounded" />
                  <div className="h-6 w-6 rounded-full bg-slate-50" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
